import moment from 'moment';
import { isEven, findNextAppropriateVisitDate } from '../utils/dateUtils';



/**
 * Check for future visits for a site
 */
export const checkForFutureVisits = (siteId, date, futureVisits) => {
  console.log('Check this date for future visits', siteId);
  let matchingItems = futureVisits.filter(visit => (visit.siteId === siteId.toString()));
  let exists = futureVisits.some(visit => {
    const visitSite = visit.siteId;
    return (visitSite === siteId.toString());
  });
  console.log('Exists::', exists);
  return matchingItems;
};

/**
 * Find next matching date for a site based on frequency and preferences
 */
export const findNextMatchingDate = (site, startDate = moment()) => {
  const { frequency, evenOrOddWeeks, preferredDay, preferredDays } = site;
  let currentDate = moment(startDate);

  const fullDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const isEvenWeek = (date) => moment(date).week() % 2 === 0;

  const getNextWeekday = (date, targetDay) => {
    console.log('Get next weekday ', date.format('YYYY-MM-DD'), targetDay);
    if (!targetDay || targetDay === 'None') {
      return date.clone();
    }

    const targetIndex = fullDays.indexOf(targetDay.toLowerCase());
    if (targetIndex === -1) {
      throw new Error('Invalid preferred day');
    }

    let daysToAdd = targetIndex - date.day();
    if (daysToAdd <= 0) daysToAdd += 7;
    console.log('Days To Add', daysToAdd);
    return date.clone().add(daysToAdd, 'days');
  };

  const getDayToCheck = () => {
    if (preferredDay && preferredDay !== 'None') {
      return preferredDay.toLowerCase();
    }
    if (preferredDays && preferredDays.length > 0) {
      return preferredDays[0].toLowerCase();
    }
    return null;
  };

  const findNextDate = () => {
    const dayToCheck = getDayToCheck();
    console.log('Day to check: %s  - Frequency: %s ', dayToCheck, frequency.toLowerCase());

    switch (frequency.toLowerCase()) {
      case 'monthly':
      case 'quarterly':
      case 'semi-annually':
      case 'on-demand':
        if (!dayToCheck) return currentDate;
        let nextDate = currentDate.clone();
        if (frequency.toLowerCase() === 'monthly') nextDate.add(1, 'month').startOf('month');
        if (frequency.toLowerCase() === 'quarterly') nextDate.add(1, 'quarter').startOf('quarter');
        if (frequency.toLowerCase() === 'semi-annually') nextDate.add(6, 'months').startOf('month');
        return getNextWeekday(nextDate, dayToCheck);

      case 'weekly':
        return dayToCheck ? getNextWeekday(currentDate, dayToCheck) : currentDate;

      case 'bi-weekly':
        if (!dayToCheck) return currentDate;
        let biWeeklyDate = getNextWeekday(currentDate, dayToCheck);
        const targetEvenWeek = evenOrOddWeeks.toLowerCase() === 'even';
        while (isEvenWeek(biWeeklyDate) !== targetEvenWeek) {
          biWeeklyDate.add(1, 'week');
        }
        return biWeeklyDate;

      case 'twice a week':
        if (preferredDays?.length) {
          const sortedPreferredDays = preferredDays
            .map(day => fullDays.indexOf(day.toLowerCase()))
            .filter(dayIndex => dayIndex !== -1)
            .sort((a, b) => a - b);

          for (let i = 0; i < sortedPreferredDays.length; i++) {
            const dayIndex = sortedPreferredDays[i];
            if (dayIndex > currentDate.day()) {
              return currentDate.clone().day(dayIndex);
            }
          }
          return currentDate.clone().add(1, 'week').day(sortedPreferredDays[0]);
        } else {
          if (currentDate.day() < 3) {
            return currentDate.day(3);
          } else if (currentDate.day() < 6) {
            return currentDate.day(6);
          } else {
            return currentDate.add(1, 'week').day(3);
          }
        }

      case 'every other day (m-w-f)':
        const nextDays = [1, 3, 5]; // Monday, Wednesday, Friday
        const nextDay = nextDays.find(day => day > currentDate.day()) || nextDays[0];
        return currentDate.day(nextDay);

      default:
        return currentDate;
    }
  };

  return findNextDate();
};

/**
 * Calculate total hours and crew hours
 */
export const calculateHours = (filteredSites) => {
  let totalHours = 0;
  const crewHours = {};

  filteredSites.forEach(site => {
    const hours = parseFloat(site.estimatedHours) || 0;
    totalHours += hours;

    if (site.crew) {
      crewHours[site.crew] = (crewHours[site.crew] || 0) + hours;
    }
  });

  return { totalHours, crewHours };
};

/**
 * Format distance from meters to miles
 */
export const formatDistance = (meters) => {
  return Math.round(Number(meters / 1609) * 100) / 100;
};

/**
 * Get background color for hours based on workload
 */
export const getBackgroundColor = (hours) => {
  if (hours > 8) return 'rgba(212,102,102,0.5)';
  if (hours > 6) return 'rgba(240,223,113,0.5)';
  if (hours > 4) return 'rgba(203,250,192,0.5)';
  return '#eee';
};

/**
 * Calculate weekly hours for crews
 */
export const calculateWeeklyHours = (weeklyRoutes, crews) => {
  let weeklyHours = {};
  console.log('calculate weekly hours.', weeklyRoutes, crews)
  // Initialize weekly hours for each crew
  crews.forEach(crew => {
    weeklyHours[crew._id] = {
      Monday: { serviceTime: 0, driveTime: 0 },
      Tuesday: { serviceTime: 0, driveTime: 0 },
      Wednesday: { serviceTime: 0, driveTime: 0 },
      Thursday: { serviceTime: 0, driveTime: 0 },
      Friday: { serviceTime: 0, driveTime: 0 },
      Saturday: { serviceTime: 0, driveTime: 0 },
      Sunday: { serviceTime: 0, driveTime: 0 },
      total: { serviceTime: 0, driveTime: 0 }
    };
  });

  // Calculate hours for each day of the week
  weeklyRoutes.forEach(dayRoute => {
    const dayName = dayRoute.day;
    dayRoute.routes.forEach(route => {
      const crewId = route.crewId;
      if (!weeklyHours[crewId]) {
        console.warn(`Undefined crewId ${crewId} on ${dayName}. Initializing...`);
        console.log(route)
        weeklyHours[crewId] = {
          Monday: { serviceTime: 0, driveTime: 0 },
          Tuesday: { serviceTime: 0, driveTime: 0 },
          Wednesday: { serviceTime: 0, driveTime: 0 },
          Thursday: { serviceTime: 0, driveTime: 0 },
          Friday: { serviceTime: 0, driveTime: 0 },
          Saturday: { serviceTime: 0, driveTime: 0 },
          Sunday: { serviceTime: 0, driveTime: 0 },
          total: { serviceTime: 0, driveTime: 0 }
        };
      }

      const serviceTime = route.totalServiceTime / 60;
      const driveTime = route.totalDriveTime / 60;

      weeklyHours[crewId][dayName].serviceTime += serviceTime;
      weeklyHours[crewId][dayName].driveTime += driveTime;
      weeklyHours[crewId].total.serviceTime += serviceTime;
      weeklyHours[crewId].total.driveTime += driveTime;
    });
  });

  return weeklyHours;
};

/**
 * Validation helpers
 */
export const validateSiteAssignment = (site, crew, scheduledDates) => {
  const errors = [];
  
  if (!site?._id) {
    errors.push('Site ID is required');
  }
  
  if (!crew?._id) {
    errors.push('Crew ID is required');
  }
  
  if (!scheduledDates || Object.keys(scheduledDates).length === 0) {
    errors.push('At least one scheduled date is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Date utilities for scheduling
 */
export const getWeekBoundaries = (date) => {
  const startOfWeek = moment(date).startOf('isoWeek'); // Monday
  const endOfWeek = moment(date).endOf('isoWeek'); // Sunday
  
  return {
    start: startOfWeek.toDate(),
    end: endOfWeek.toDate(),
    weekNumber: moment(date).isoWeek(),
    isEvenWeek: moment(date).isoWeek() % 2 === 0
  };
};

/**
 * Filter sites by frequency requirements
 */
export const filterSitesByFrequency = (sites, frequency) => {
  return sites.filter(site => site.frequency === frequency);
};

/**
 * Check if a date matches frequency requirements
 */
export const matchesFrequencyRequirements = (site, date) => {
  const { frequency, preferredDay, evenOrOddWeeks, preferredDays } = site;
  const dayOfWeek = moment(date).format('dddd');
  const weekNumber = moment(date).week();
  const isEvenWeek = weekNumber % 2 === 0;
  
  switch (frequency?.toLowerCase()) {
    case 'daily':
      return true;
      
    case 'weekly':
      return preferredDay === dayOfWeek;
      
    case 'bi-weekly':
      const targetEvenWeek = evenOrOddWeeks?.toLowerCase() === 'even';
      return preferredDay === dayOfWeek && isEvenWeek === targetEvenWeek;
      
    case 'twice a week':
      return preferredDays?.includes(dayOfWeek);
      
    case 'every other day (m-w-f)':
      return ['Monday', 'Wednesday', 'Friday'].includes(dayOfWeek);
      
    case 'monthly':
    case 'quarterly':
    case 'semi-annually':
    case 'annually':
    case 'on-demand':
      // These require explicit scheduling
      return false;
      
    default:
      return false;
  }
};

/**
 * Calculate estimated travel time between sites
 */
export const calculateTravelTime = (site1, site2) => {
  if (!site1 || !site2) return 0;
  
  const lat1 = site1.latitude || site1.location?.coordinates?.[1];
  const lng1 = site1.longitude || site1.location?.coordinates?.[0];
  const lat2 = site2.latitude || site2.location?.coordinates?.[1];
  const lng2 = site2.longitude || site2.location?.coordinates?.[0];
  
  if (!lat1 || !lng1 || !lat2 || !lng2) return 0;
  
  // Haversine formula for distance
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  // Estimate travel time (assuming 30 mph average in service areas)
  return (distance / 30) * 60; // Return in minutes
};

/**
 * Group sites by location proximity
 */
export const groupSitesByProximity = (sites, maxDistanceMiles = 5) => {
  const groups = [];
  const used = new Set();
  
  sites.forEach(site => {
    if (used.has(site._id)) return;
    
    const group = [site];
    used.add(site._id);
    
    sites.forEach(otherSite => {
      if (used.has(otherSite._id) || site._id === otherSite._id) return;
      
      const distance = calculateTravelTime(site, otherSite) * 30 / 60; // Convert back to distance
      if (distance <= maxDistanceMiles) {
        group.push(otherSite);
        used.add(otherSite._id);
      }
    });
    
    groups.push(group);
  });
  
  return groups;
};

/**
 * Debug helpers
 */
export const debugLogState = (label, state) => {
  if (process.env.NODE_ENV === 'development') {
    console.group(`🔍 Debug: ${label}`);
    console.log('State:', state);
    console.groupEnd();
  }
};

export const debugLogAssignment = (site, crew, scheduledDates) => {
  if (process.env.NODE_ENV === 'development') {
    console.group('🎯 Assignment Debug');
    console.log('Site:', site?.name, site?._id);
    console.log('Crew:', crew?.name, crew?._id);
    console.log('Scheduled Dates:', scheduledDates);
    console.groupEnd();
  }
};
